import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

/**
 * Tag autocomplete for support ticket sidebar.
 *
 * data-controller="support--tag-autocomplete"
 *
 * Fetches matching tags as the user types and displays a dropdown of suggestions.
 * On selection, fills the input and submits the form.
 */
export default class extends Controller {
  static targets = ["input", "dropdown", "form"]
  static values = {
    url: String, // e.g. /s:group:branches/support/tickets/tags/suggestions
  }

  connect() {
    this._debounceTimer = null
    this._selectedIndex = -1

    this._onOutsideClick = this._onOutsideClick.bind(this)
    document.addEventListener("click", this._onOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this._onOutsideClick)
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
  }

  // Called on input event
  onInput() {
    const query = this.inputTarget.value.trim()
    if (query.length < 1) {
      this._hideDropdown()
      return
    }

    // Debounce requests
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._fetchSuggestions(query), 200)
  }

  // Handle keyboard navigation in the dropdown
  onKeydown(event) {
    if (
      !this.hasDropdownTarget ||
      this.dropdownTarget.classList.contains("hidden")
    ) {
      return
    }

    const items = this.dropdownTarget.querySelectorAll("[data-tag-value]")
    if (items.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this._selectedIndex = Math.min(
          this._selectedIndex + 1,
          items.length - 1,
        )
        this._highlightItem(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this._selectedIndex = Math.max(this._selectedIndex - 1, 0)
        this._highlightItem(items)
        break
      case "Enter":
        if (this._selectedIndex >= 0 && this._selectedIndex < items.length) {
          event.preventDefault()
          this._selectTag(items[this._selectedIndex].dataset.tagValue)
        }
        break
      case "Escape":
        this._hideDropdown()
        break
    }
  }

  // Called when a suggestion is clicked
  selectSuggestion(event) {
    const tag = event.currentTarget.dataset.tagValue
    if (tag) {
      this._selectTag(tag)
    }
  }

  // Private methods

  async _fetchSuggestions(query) {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await get(url.toString(), { responseKind: "json" })

      if (!response.ok) return

      const tags = await response.json
      this._renderDropdown(tags)
    } catch {
      // Silently fail — autocomplete is a nice-to-have
    }
  }

  _renderDropdown(tags) {
    if (!this.hasDropdownTarget) return

    this._selectedIndex = -1

    if (tags.length === 0) {
      this._hideDropdown()
      return
    }

    this.dropdownTarget.innerHTML = tags
      .map(
        (tag) =>
          `<button type="button"
                  data-tag-value="${this._escapeHtml(tag)}"
                  data-action="click->support--tag-autocomplete#selectSuggestion"
                  class="w-full text-left px-2.5 py-1.5 text-xs hover:bg-muted transition-colors text-foreground">
            ${this._escapeHtml(tag)}
          </button>`,
      )
      .join("")

    this.dropdownTarget.classList.remove("hidden")
  }

  _selectTag(tag) {
    if (this.hasInputTarget) {
      this.inputTarget.value = tag
    }
    this._hideDropdown()

    // Submit the form to add the tag
    if (this.hasFormTarget) {
      this.formTarget.requestSubmit()
    }
  }

  _hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
      this.dropdownTarget.innerHTML = ""
    }
    this._selectedIndex = -1
  }

  _highlightItem(items) {
    items.forEach((item, i) => {
      item.classList.toggle("bg-muted", i === this._selectedIndex)
    })
  }

  _onOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this._hideDropdown()
    }
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
